import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { listSessions } from '../../services/supportChat.services';
import SupportChatWindow from './SupportChatWindow';
import { TrimText, getUser } from '../../../../utils/commonfunction';
import { SOCKET_CLIENT, authenticateSocket } from '../../../../utils/socket';
import { ROLE, DYNAMIC_VARIABLES } from '../../../../utils/Constants';

const SupportChatInterface = () => {
    const [allSessions, setAllSessions] = useState([]);
    const [activeSession, setActiveSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('open'); // active sessions by default

    // Use ref to keep track of current active session for the polling interval
    const activeSessionRef = React.useRef(null);

    const fetchSessions = () => {
        // We don't set loading to true for interval fetches to avoid UI flicker
        listSessions().then((res) => {
            if (res?.data) {
                setAllSessions(res.data);
                // Only auto-select the first session if none is currently selected
                if (res.data.length > 0 && !activeSessionRef.current) {
                    activeSessionRef.current = res.data[0];
                    setActiveSession(res.data[0]);
                } else if (activeSessionRef.current) {
                    // Update active session data from the new list if it exists
                    const updatedActive = res.data.find(s => s.id === activeSessionRef.current.id);
                    if (updatedActive) {
                        activeSessionRef.current = updatedActive;
                        setActiveSession(updatedActive);
                    }
                }
            }
            setLoading(false);
        }).catch((err) => {
            console.error("Error fetching support chat sessions:", err);
            setLoading(false);
        });
    };

    useEffect(() => {
        const admin = getUser(ROLE.SUPER_ADMIN);
        if (admin?.token) {
            authenticateSocket(admin.token);
        }

        fetchSessions();
        // Keep polling as a slow fallback (30 seconds)
        const interval = setInterval(fetchSessions, 30000);

        // Listen for global support message events to update the list in real-time
        const handleNewGlobalMessage = (data) => {
            console.log("📢 Global support notification:", data);
            const { session_id, last_message, last_message_time, sender_type } = data;

            setAllSessions((prevSessions) => {
                const sessionIndex = prevSessions.findIndex(s => s.id === session_id);
                if (sessionIndex !== -1) {
                    const updatedSessions = [...prevSessions];
                    const session = { ...updatedSessions[sessionIndex] };

                    session.last_message = last_message;
                    session.last_message_time = last_message_time;

                    // Increment unread count only if:
                    // 1. It's not the active session being viewed
                    // 2. The sender is NOT an admin
                    const isActive = activeSessionRef.current && activeSessionRef.current.id == session_id;
                    const adminRoles = ['admin', 'sub-admin', 'staff', 'superAdmin'];
                    const isFromOther = !adminRoles.includes(sender_type?.toLowerCase()) && sender_type !== 'admin';

                    if (!isActive && isFromOther) {
                        session.unread_count = (session.unread_count || 0) + 1;
                    } else if (isActive) {
                        session.unread_count = 0; // Explicitly clear for active session
                    }


                    // Update status if provided (e.g. session reopened)
                    if (data.status) {
                        session.status = data.status;
                        // Also update active session if it's the currently viewed one
                        if (isActive && session.status === 'open') {
                            activeSessionRef.current = { ...activeSessionRef.current, status: 'open' };
                            setActiveSession((prev) => ({ ...prev, status: 'open' }));
                        }
                    }

                    // Move to top
                    updatedSessions.splice(sessionIndex, 1);
                    updatedSessions.unshift(session);
                    return updatedSessions;
                } else {

                    // If session not found, maybe it's a new session, refresh the list
                    fetchSessions();
                    return prevSessions;
                }
            });
        };

        const onConnectGlobal = () => {
            console.log("⚡ Socket connected globally for admin dashboard");
        };

        SOCKET_CLIENT.on("connect", onConnectGlobal);
        SOCKET_CLIENT.on("new_support_message_admin", handleNewGlobalMessage);

        return () => {
            clearInterval(interval);
            SOCKET_CLIENT.off("connect", onConnectGlobal);
            SOCKET_CLIENT.off("new_support_message_admin", handleNewGlobalMessage);
        };
    }, []);

    const handleSessionSelect = (session) => {
        activeSessionRef.current = session;
        setActiveSession(session);

        // Clear unread count locally and add session to list if missing
        setAllSessions((prevSessions) => {
            const exists = prevSessions.some((s) => s.id === session.id);
            if (exists) {
                return prevSessions.map((s) =>
                    s.id === session.id ? { ...s, ...session, unread_count: 0 } : s
                );
            } else {
                return [{ ...session, unread_count: 0 }, ...prevSessions];
            }
        });
    };


    const filteredSessions = allSessions.filter(s => filter === 'all' || s.status === filter);

    return (
        <div className="container-fluid h-100">
            <div className="row h-100">
                {/* 1. SESSIONS LIST (Sidebar) */}
                <div className="col-12 col-md-4 border-end d-flex flex-column p-0" style={{ height: "80vh" }}>
                    <div className="p-3 bg-light border-bottom d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Support Chats 💬</h5>
                        <select
                            className="form-select form-select-sm w-auto"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        >
                            <option value="open">Open</option>
                            <option value="closed">Closed</option>
                            <option value="all">All</option>
                        </select>
                    </div>

                    {/* List Container - Scrollable */}
                    <div className="list-group list-group-flush flex-grow-1 overflow-auto">
                        {loading && allSessions.length === 0 ? (
                            <div className="p-4 text-center">Loading...</div>
                        ) : filteredSessions.length === 0 ? (
                            <div className="p-4 text-center text-muted">No sessions found</div>
                        ) : (
                            filteredSessions.map((session) => {
                                const defaultAvatarUrl = DYNAMIC_VARIABLES.SERVER_URL ? `${DYNAMIC_VARIABLES.SERVER_URL}/public/uploads/users/default.jpg` : 'http://localhost:5000/public/uploads/users/default.jpg';
                                const profileImg = session.user?.profile_image || session.astrologer?.profile_image || defaultAvatarUrl;
                                const name = session.user?.name || session.astrologer?.name || "Anonymous";
                                const type = session.user ? "User" : "Astrologer";

                                return (
                                    <a
                                        href="#"
                                        key={session.id}
                                        className={`list-group-item list-group-item-action py-3 border-0 ${activeSession?.id === session.id ? "active" : ""}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleSessionSelect(session);
                                        }}
                                    >
                                        <div className="d-flex align-items-center">
                                            {/* Profile Picture */}
                                            <div className="me-3">
                                                <img
                                                    src={profileImg}
                                                    alt={name}
                                                    className="rounded-circle"
                                                    style={{
                                                        width: "48px",
                                                        height: "48px",
                                                        objectFit: "cover",
                                                        border: "2px solid #e9ecef",
                                                    }}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = DYNAMIC_VARIABLES.SERVER_URL ? `${DYNAMIC_VARIABLES.SERVER_URL}/public/uploads/users/default.jpg` : 'http://localhost:5000/public/uploads/users/default.jpg';
                                                    }}
                                                />
                                            </div>

                                            {/* Name, Last Message, Time */}
                                            <div className="flex-grow-1 min-width-0">
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <h6 className="mb-0 text-truncate" style={{ maxWidth: "150px" }}>
                                                        {name}
                                                        <small className="badge bg-info ms-2" style={{ fontSize: '10px' }}>{type}</small>
                                                    </h6>
                                                    <small className="text-muted flex-shrink-0 ms-2">
                                                        {session.last_message_time ? moment(session.last_message_time).format("hh:mm A") : ""}
                                                        {session.unread_count > 0 && (
                                                            <div className="text-end">
                                                                <span className="badge bg-danger rounded-circle p-1 ms-2" style={{ minWidth: '10px', height: '10px', display: 'inline-flex', padding: 0 }}>
                                                                </span>
                                                            </div>
                                                        )}
                                                    </small>
                                                </div>
                                                <p className="mb-0 small text-muted text-truncate">
                                                    {TrimText(session.last_message, 40) || "No messages yet"}
                                                </p>
                                                {session.status === 'closed' && (
                                                    <span className="badge bg-secondary mt-1">Closed</span>
                                                )}
                                            </div>
                                        </div>
                                    </a>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* 2. CHAT WINDOW (Main Area) */}
                <div className="col-12 col-md-8 d-flex flex-column p-0" style={{ height: "80vh" }}>
                    <SupportChatWindow
                        activeSession={activeSession}
                        onSessionUpdate={fetchSessions}
                        onSelectSession={(session) => {
                            setFilter('open');
                            handleSessionSelect(session);
                            fetchSessions();
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default SupportChatInterface;
